import { z } from 'zod'
import { HistorizedScopedModel, HistorizedScopedModelFieldNames } from './base-models'
import { BidderSeat, BidderSeatProduct } from './bidder-seat'
import { MediaType } from './media-types'
import { StacksBySite } from './stack'
import { compareNames } from './sort'

/**
 * The three ways a seat is integrated, and the three sections of the rule editor.
 *
 * They are `(type, platform)` pairs rather than a stored field: `client_side` and
 * `server_side_deprecated` are both `type: 'adm'` and only `platform` tells them apart, while
 * `server_side` is every non-`adm` type. See `isCrossInventoryServerSeat` for the same split
 * expressed from the seat's side.
 */
export const BIDDER_RULE_SECTIONS = ['client_side', 'server_side_deprecated', 'server_side'] as const
export const BidderRuleSectionSchema = z.enum(BIDDER_RULE_SECTIONS)
export type BidderRuleSection = z.infer<typeof BidderRuleSectionSchema>

export const BIDDER_RULE_SECTION_LABELS: Record<BidderRuleSection, string> = {
  client_side: 'Client-side',
  server_side_deprecated: 'Server-side One (legacy)',
  server_side: 'Server-side',
}

/**
 * Mirrors `productOfBidderSeatType`. Asked of `type` first, because `platform: 'server'` is true of
 * both the deprecated ADM seat and every Prebid Server one — testing `platform` alone would fold the
 * two server-side sections into one.
 */
export const sectionOfBidderSeat = (seat: Pick<BidderSeat, 'type' | 'platform'>): BidderRuleSection =>
  seat.type !== 'adm' ? 'server_side' : seat.platform === 'client' ? 'client_side' : 'server_side_deprecated'

/** Which product a section's seats belong to — `client_side` and `server_side_deprecated` both stay inside One. */
export const productOfBidderRuleSection = (section: BidderRuleSection): BidderSeatProduct => (section === 'server_side' ? 'pbs' : 'adm')

/** The sections a product can select seats from. The inverse of `productOfBidderRuleSection`. */
export const BIDDER_RULE_SECTIONS_BY_PRODUCT: Record<BidderSeatProduct, readonly BidderRuleSection[]> = {
  adm: ['client_side', 'server_side_deprecated'],
  pbs: ['server_side'],
}

export const AutoAddNewBidderSeatsSchema = z.object({
  client_side: z.boolean(),
  server_side_deprecated: z.boolean(),
  server_side: z.boolean(),
})
export type AutoAddNewBidderSeats = z.infer<typeof AutoAddNewBidderSeatsSchema>

export const NO_AUTO_ADD: AutoAddNewBidderSeats = { client_side: false, server_side_deprecated: false, server_side: false }

/**
 * What a hand-made rule starts with. Auto-add on is the safer default of the two: a new seat that
 * silently misses a rule is invisible until someone notices the bidder never bids, whereas one that
 * joins a rule it should not be in shows up in the editor.
 *
 * Set on every section, including any the scope cannot edit yet — the flag then already means the
 * right thing if that product is bought later.
 */
export const ALL_AUTO_ADD: AutoAddNewBidderSeats = { client_side: true, server_side_deprecated: true, server_side: true }

/**
 * A named allow-list of bidder seats, each with the media types it may bid on. Not a predicate
 * engine: membership is the only condition, and the rule is bound to inventory by the stacks that
 * reference it rather than by anything on the rule.
 *
 * One rule spans both products. A seat id belonging to the other product is inert at snapshot build
 * — each product's builder only ever resolves the seat ids its own mappings carry — so a rule may
 * safely select from all three sections at once.
 *
 * A seat that is absent from `selectedBidderSeats` is disabled. The editor's third media-type state,
 * "not compatible", is derived from the bidder catalog at render time and never stored.
 */
export interface BidderRule extends HistorizedScopedModel {
  name: string
  selectedBidderSeats: { id: string; mediaTypes: MediaType[] }[]
  autoAddNewBidderSeats: AutoAddNewBidderSeats
  /** The rule a scope's stacks fall back to. Seeded on onboarding, never deleted. */
  isDefault: boolean
  /**
   * Which products this rule is the default for. Only meaningful when `isDefault`.
   *
   * A scope onboarded after the unification holds one default covering both products; a scope
   * migrated from the two legacy collections holds one per product, disambiguated by a name suffix.
   * Resolution therefore asks for the product rather than matching the name.
   */
  defaultFor: BidderSeatProduct[]
  /** Stacks using this rule, computed on read. */
  usedInStacks?: StacksBySite
  /**
   * Set aside without being deleted. Absent on every rule written before archiving existed, so the
   * live set is `archived !== true` rather than `archived === false`.
   *
   * Never true on an `isDefault` rule: a default is what a stack falls back to, so there is no state
   * in which putting it out of reach is safe. `ensureRuleIsArchivable` is where that is enforced.
   */
  archived?: boolean
}

export type CreateBidderRule = Omit<BidderRule, HistorizedScopedModelFieldNames | 'usedInStacks' | 'archived'>
export type UpdateBidderRule = Omit<BidderRule, Exclude<HistorizedScopedModelFieldNames, 'id'> | 'usedInStacks' | 'archived'>

/**
 * The name given to a default created after the unification, which covers every section at once.
 *
 * The two legacy defaults were both called this, in separate collections. Merging them into one
 * collection would have made the pair indistinguishable, so the migration suffixes each with the
 * product it came from — hence three possible names in the wild, and hence `isDefault`/`defaultFor`
 * rather than a name comparison anywhere that has to *find* the default. The suffix names the
 * product, not a seat type: a One rule may select server-side seats too.
 */
export const DEFAULT_BIDDER_RULE_NAME = 'All active bidders'
export const MIGRATED_DEFAULT_BIDDER_RULE_NAMES: Record<BidderSeatProduct, string> = {
  adm: `${DEFAULT_BIDDER_RULE_NAME} (One)`,
  pbs: `${DEFAULT_BIDDER_RULE_NAME} (Node)`,
}

export const isDefaultBidderRuleFor = (rule: Pick<BidderRule, 'isDefault' | 'defaultFor'>, product: BidderSeatProduct): boolean => rule.isDefault && rule.defaultFor.includes(product)

/**
 * How a bidder rule list reads: the scope's default rules first, then the others by name.
 *
 * `isDefault === true` rather than `Number(isDefault)`: a migrated rule that never had the field written gives
 * `NaN`, which is falsy and would let the name decide, putting a user's "AAA" above the default.
 */
export const byDefaultThenName = (a: Pick<BidderRule, 'isDefault' | 'name'>, b: Pick<BidderRule, 'isDefault' | 'name'>): number =>
  Number(b.isDefault === true) - Number(a.isDefault === true) || compareNames(a.name, b.name)
